import PropTypes from 'prop-types';
import {useState} from 'react';

import Button from 'components/Button';
import OnOffButton from 'components/Button/OnOff';
import ClinicalAnnotationsBarChart from 'components/ClinicalAnnotation/BarChart';
import Loading from 'components/Loading';
import SummaryAnnotationTable from 'components/SummaryAnnotation/Table';
import KyError from 'components/errors/KyError';
import InfoIcon from 'components/icons/Info';
import Link from 'components/links/Link';
import AlleleNote from 'components/shared/AlleleNote';
import useAppContext from 'conf/AppContext';
import usePediatricContext from 'context/Pediatric';
import {useGet} from 'helpers/KyHooks';

import './index.scss';


const propTypes = {
  objCls: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
};

export default function SummaryAnnotationsTab({objCls, id}) {
  const appContext = useAppContext();
  const pediatricContext = usePediatricContext();
  const target = objCls === 'combination' ? id : `${objCls}/${id}`;
  const {response, error} = useGet(`site/tab/clinicalAnnotations/${target}`, {
    cacheResponse: !appContext.isPreview,
  });

  const [filter, setFilter] = useState(null);

  let body = <Loading />;

  let barChart = <div className="caFigure caFigure--loading figure figure--floatRight"><Loading /></div>;

  const pedFocusHandler = () => pediatricContext.togglePediatricMode('caTab');

  if (error) {
    body = <KyError kyError={error} />;
  } else if (response) {
    if (response?.data?.length > 0) {
      body = (
        <SummaryAnnotationTable
          data={response?.data}
          objCls={objCls}
          updatedFilters={filter}
        />
      );
      barChart = (
        <ClinicalAnnotationsBarChart
          clinicalAnnotations={response?.data}
          filterChangeFn={setFilter}
          captionTitle="Annotations by "
          className="figure--floatRight"
        />
      );
    } else {
      body = <p className="noData">No summary annotations.</p>;
      barChart = undefined;
    }
  }

  return (
    <div className="clinicalAnnotationsTab">
      <div className="float-right" style={{width: '15em', fontSize: '.94em'}}>
        <OnOffButton
          label="Focus on Pediatrics"
          on={pediatricContext.isPediatricMode}
          stateful={false}
          actionHandler={pedFocusHandler}
          tabIndex={0}
        />
      </div>

      <h3>Summary Annotations</h3>

      <div className="clearfix">
        {barChart}
        <div className="island-bottom">
          <p>
            Summary annotations (formerly &quot;clinical annotations&quot;) provide information about
            variant-drug pairs based primarily on variant annotations and incorporating variant-specific prescribing
            guidance from clinical guidelines and FDA-approved drug labels, when available. Curators manually review
            annotations and create genotype-based summaries describing the phenotypic impact of the variant. Each
            summary annotation is assigned a <Link href="/page/clinAnnLevels">Level of Evidence</Link>, which is
            generally informed by the summary annotation&apos;s <Link href="/page/clinAnnScoring">score</Link>.
          </p>
          <AlleleNote />
        </div>
      </div>

      <div className="caTabIntroHelp">
        <div>
          <Button className="btn-outline-secondary" href="/summaryAnnotations"><InfoIcon /> Read more about Summary Annotations</Button>
        </div>
        <div><Button className="btn-outline-secondary" href="/variantAnnotations"><InfoIcon /> Read more about Variant Annotations</Button></div>
      </div>
      
      {body}
    </div>
  );
}
SummaryAnnotationsTab.propTypes = propTypes;
